import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColumnVisibilityModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { createScopedJSONStorage, scopedLocalStorage } from '@/lib/browser/scopedStorage';
import { GrownByScope, SampleSearchKind, type RoleWithPrivilegesDto, SampleSearchCriteria } from '@/api/ApiClient';
import { reviveSampleSearchCriteriaDates } from '@/lib/utils/reviveSampleSearchCriteriaDates';
import type { BulkSampleRowType } from '@/components/tables/bulk/BulkSampleGrid';

const STORAGE_KEY = 'bulkFilters';
export const COLUMN_VISIBILITY_STORAGE_KEY = 'bulkColumnVisibility';

/**
 * Column visibility modes
 */
export type ColumnVisibilityMode = 'all' | 'default' | 'custom';

/**
 * Always visible columns - these cannot be hidden
 * Includes anchor columns from each group (shown when group is collapsed)
 * Note: Checkbox column is handled automatically by MUI DataGrid and doesn't need to be in this list
 */
export const ALWAYS_VISIBLE_COLUMNS = new Set<string>([
  'growthLabel', // Growth Run anchor
  'projectLabel', // Project anchor
  'sampleLabel', // Sample anchor
  'synInstrument', // Synthesis anchor
  'compInstrument', // Compounding anchor
  'startingMaterial1Date', // Starting Material 1 anchor
  'startingMaterial2Date', // Starting Material 2 anchor
]);

/**
 * Columns that are NOT in the default visibility set
 */
export const NON_DEFAULT_COLUMNS = new Set<string>([
  'reportingCategory',
  'pieces',
  'shipped',
  'quality',
  'startingMaterial1RawVendors',
  'startingMaterial2RawVendors',
]);

/**
 * Get all column field names from the column definitions
 * This will be computed dynamically in BulkSampleGrid
 */
export function getDefaultColumnVisibility(allColumns: string[]): GridColumnVisibilityModel {
  const model: GridColumnVisibilityModel = {};
  for (const field of allColumns) {
    // Always visible columns are always true
    if (ALWAYS_VISIBLE_COLUMNS.has(field)) {
      model[field] = true;
    } else {
      // Default: visible unless in NON_DEFAULT_COLUMNS
      model[field] = !NON_DEFAULT_COLUMNS.has(field);
    }
  }
  return model;
}

/**
 * Get "all columns visible" model
 */
export function getAllColumnVisibility(allColumns: string[]): GridColumnVisibilityModel {
  const model: GridColumnVisibilityModel = {};
  for (const field of allColumns) {
    model[field] = true;
  }
  return model;
}

/**
 * Default filter criteria for Bulk sample search.
 *
 * Note: the backend will force `techniqueId = "Bulk"` when not provided.
 */
export const defaultFilterCriteria: SampleSearchCriteria = {
  sampleLabel: '',
  projectId: '',
  reportingCategory: '',
  description: '',
  grownAfter: undefined,
  grownBefore: undefined,
  includeSplitPieces: false,
  includeFinalized: true,
  onlyPending: false,
  onlySubmitted: false,
  materials: [],
  multiSampleGrowthOnly: false,
  shipped: '',
  grownByScope: GrownByScope.Any,
  onlyPreparedMaterials: false,
  kind: SampleSearchKind.Active,
};

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  projectLabel: true,
  sampleLabel: true,
  material: true,
  reportingCategory: true,
  quality: true,
  pieces: true,
  shipped: true,
};

export function loadStoredColumnVisibility(): GridColumnVisibilityModel {
  if (typeof window === 'undefined') return defaultColumnVisibilityModel;

  try {
    const stored = scopedLocalStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) return defaultColumnVisibilityModel;
    return JSON.parse(stored) as GridColumnVisibilityModel;
  } catch {
    return defaultColumnVisibilityModel;
  }
}

interface BulkFilterStore {
  /** Current filter criteria for bulk search */
  filters: SampleSearchCriteria;
  /** True while the bulk grid is actively fetching results */
  isSearching: boolean;
  /** Sets search-in-progress state (not persisted) */
  setIsSearching: (isSearching: boolean) => void;
  /** Updates the filter criteria and persists to localStorage */
  setFilters: (filters: SampleSearchCriteria) => void;
  /** Resets filters to default criteria */
  resetFilters: () => void;
  /** Current pagination state */
  paginationModel: { page: number; pageSize: number };
  /** Updates pagination state, accepts either new state or updater function */
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
  /** Current rows in the grid (for selection state) */
  rows: BulkSampleRowType[];
  /** Set the current rows */
  setRows: (rows: BulkSampleRowType[]) => void;
  /** Collaborator role privileges from the latest bulk sample load (for grid action gating). */
  bulkRolePrivileges: RoleWithPrivilegesDto[];
  setBulkRolePrivileges: (privileges: RoleWithPrivilegesDto[]) => void;
  /** Current selection model */
  rowSelectionModel: GridRowSelectionModel;
  /** Set the selection model */
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  /** Refresh callback for the grid (optional filters when applying search from the panel). */
  refreshCallback: ((overrideFilters?: SampleSearchCriteria) => void) | null;
  /** Set the refresh callback */
  setRefreshCallback: (callback: ((overrideFilters?: SampleSearchCriteria) => void) | null) => void;
  /** Column visibility mode */
  columnVisibilityMode: ColumnVisibilityMode;
  /** Set column visibility mode */
  setColumnVisibilityMode: (mode: ColumnVisibilityMode) => void;
  /** Custom column visibility model (only used when mode is 'custom') */
  customColumnVisibility: GridColumnVisibilityModel;
  /** Set custom column visibility model */
  setCustomColumnVisibility: (model: GridColumnVisibilityModel) => void;
  /** Column group IDs that are collapsed (only anchor column visible) */
  collapsedColumnGroups: string[];
  /** Toggle a column group's collapsed state */
  toggleColumnGroupCollapsed: (groupId: string) => void;
}

/**
 * Zustand store for managing bulk filters and pagination.
 * Uses Zustand's persist middleware for automatic localStorage persistence.
 */
export const useBulkFilterStore = create<BulkFilterStore>()(
  persist(
    (set, get) => ({
      filters: defaultFilterCriteria,
      isSearching: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters) => set({ filters: reviveSampleSearchCriteriaDates(filters) }),
      resetFilters: () => set({ filters: defaultFilterCriteria }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
      rows: [],
      setRows: (rows) => set({ rows }),
      bulkRolePrivileges: [],
      setBulkRolePrivileges: (bulkRolePrivileges) => set({ bulkRolePrivileges }),
      rowSelectionModel: { ids: new Set(), type: 'include' },
      setRowSelectionModel: (model) => set({ rowSelectionModel: model }),
      refreshCallback: null,
      setRefreshCallback: (callback) => set({ refreshCallback: callback }),
      columnVisibilityMode: 'default',
      setColumnVisibilityMode: (mode) => set({ columnVisibilityMode: mode }),
      customColumnVisibility: {},
      setCustomColumnVisibility: (model) => set({ customColumnVisibility: model }),
      collapsedColumnGroups: [],
      toggleColumnGroupCollapsed: (groupId) =>
        set((state) => {
          const set = new Set(state.collapsedColumnGroups);
          if (set.has(groupId)) set.delete(groupId);
          else set.add(groupId);
          return { collapsedColumnGroups: Array.from(set) };
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createScopedJSONStorage(),
      partialize: (state) => ({
        filters: state.filters,
        paginationModel: state.paginationModel,
        columnVisibilityMode: state.columnVisibilityMode,
        customColumnVisibility: state.customColumnVisibility,
        collapsedColumnGroups: state.collapsedColumnGroups,
      }),
      merge: (persistedState, currentState) => {
        const p = persistedState as
          | Partial<
              Pick<
                BulkFilterStore,
                | 'filters'
                | 'paginationModel'
                | 'columnVisibilityMode'
                | 'customColumnVisibility'
                | 'collapsedColumnGroups'
              >
            >
          | undefined
          | null;
        const c = currentState as BulkFilterStore;
        if (!p) return c;
        return {
          ...c,
          ...p,
          filters: reviveSampleSearchCriteriaDates({
            ...defaultFilterCriteria,
            ...(p.filters ?? {}),
          }),
          paginationModel: p.paginationModel ?? c.paginationModel,
          columnVisibilityMode: p.columnVisibilityMode ?? c.columnVisibilityMode,
          customColumnVisibility: p.customColumnVisibility ?? c.customColumnVisibility,
          collapsedColumnGroups: p.collapsedColumnGroups ?? c.collapsedColumnGroups,
        };
      },
    }
  )
);
